import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  CalendarDays,
  Users,
  CreditCard,
  MessageSquare,
  Settings,
  Home,
  Search,
  Bell,
  Plus,
  Check,
  ArrowUpRight,
  Clock,
  Wallet,
  ListChecks,
  Sun,
  Moon,
  ChevronRight,
  ChevronLeft,
  Stethoscope,
  Phone,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react';
import Button from '../ui/primitives/Button';
import Card from '../ui/primitives/Card';
import Badge from '../ui/primitives/Badge';
import type { BadgeTone } from '../ui/primitives/Badge';
import PageHeader from '../ui/primitives/PageHeader';
import StatTile from '../ui/primitives/StatTile';
import Avatar from '../ui/primitives/Avatar';
import Table from '../ui/primitives/Table';
import type { TableColumn } from '../ui/primitives/Table';
import './DesignPrototypePage.css';

/* ---------------------------------------------------------------------------
   Elen1 — Design system prototype (reference only)
   A self-contained, navigable "Clinical professional" app shell. Clicking the
   left nav switches the main area between fully-designed mock screens (Hoy,
   Agenda, Clientes) so the owner can judge the direction.
   100% presentational mock data; not wired to any store or API.
--------------------------------------------------------------------------- */

type Theme = 'light' | 'dark';
type NavKey =
  | 'hoy'
  | 'agenda'
  | 'clientes'
  | 'cobros'
  | 'mensajes'
  | 'ajustes'
  | 'reportes'
  | 'inventario';

// --- nav model (presentational mock) ---------------------------------------
type NavEntry = { key: NavKey; label: string; sub?: string; icon: typeof Home };

const PRIMARY_NAV: NavEntry[] = [
  { key: 'hoy', label: 'Hoy', sub: 'Today', icon: Home },
  { key: 'agenda', label: 'Agenda', sub: 'Calendar', icon: CalendarDays },
  { key: 'clientes', label: 'Clientes', sub: 'Clients', icon: Users },
  { key: 'cobros', label: 'Cobros', sub: 'Billing', icon: CreditCard },
  { key: 'mensajes', label: 'Mensajes', sub: 'Messages', icon: MessageSquare },
  { key: 'ajustes', label: 'Ajustes', sub: 'Settings', icon: Settings },
];

const MORE_NAV: NavEntry[] = [
  { key: 'reportes', label: 'Reportes', icon: ArrowUpRight },
  { key: 'inventario', label: 'Inventario', icon: ListChecks },
];

// initials helper for mock avatars
function initialsOf(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

// ===========================================================================
//  Shell chrome (nav rail + top bar)
// ===========================================================================
function NavGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="proto-navgroup">
      {label && <div className="proto-navgroup__label">{label}</div>}
      <nav className="proto-nav">{children}</nav>
    </div>
  );
}

function NavItem({
  icon: Icon,
  label,
  sub,
  active,
  onSelect,
}: {
  icon: typeof Home;
  label: string;
  sub?: string;
  active?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={active ? 'proto-navitem proto-navitem--active' : 'proto-navitem'}
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
    >
      <Icon strokeWidth={2} />
      <span>{label}</span>
      {sub && <span className="proto-navitem__sub">{sub}</span>}
    </button>
  );
}

function NavRail({ active, onSelect }: { active: NavKey; onSelect: (key: NavKey) => void }) {
  return (
    <aside className="proto-rail">
      <div className="proto-brand">
        <span className="proto-brand__mark">E</span>
        <span className="proto-brand__name">Elen1</span>
      </div>
      <NavGroup label="Principal">
        {PRIMARY_NAV.map((item) => (
          <NavItem
            key={item.key}
            icon={item.icon}
            label={item.label}
            sub={item.sub}
            active={active === item.key}
            onSelect={() => onSelect(item.key)}
          />
        ))}
      </NavGroup>
      <NavGroup label="Más (More)">
        {MORE_NAV.map((item) => (
          <NavItem
            key={item.key}
            icon={item.icon}
            label={item.label}
            active={active === item.key}
            onSelect={() => onSelect(item.key)}
          />
        ))}
      </NavGroup>
      <div className="proto-rail__spacer" />
      <div className="proto-rail__foot">
        <Avatar initials="DR" tone="accent" />
        <div>
          <strong>Dra. Renata</strong>
          <small>Recepción · Admin</small>
        </div>
      </div>
    </aside>
  );
}

function TopBar({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  return (
    <div className="proto-topbar">
      <div className="proto-topbar__clinic">
        <strong>Clínica Aurora</strong>
        <small>Sede Polanco</small>
      </div>
      <label className="proto-search">
        <Search strokeWidth={2} />
        <input placeholder="Buscar cliente, cita o factura… (Search)" aria-label="Buscar" />
      </label>
      <div className="proto-topbar__actions">
        <button
          type="button"
          className="proto-iconbtn"
          onClick={onToggleTheme}
          aria-label={theme === 'dark' ? 'Modo claro (Light mode)' : 'Modo oscuro (Dark mode)'}
          title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
        >
          {theme === 'dark' ? <Sun strokeWidth={2} /> : <Moon strokeWidth={2} />}
        </button>
        <button type="button" className="proto-iconbtn" aria-label="Notificaciones (Notifications)">
          <Bell strokeWidth={2} />
        </button>
        <Avatar initials="DR" />
      </div>
    </div>
  );
}

// ===========================================================================
//  Screen 1 — Hoy / Today (dashboard)
// ===========================================================================
type SchedStatus = { tone: BadgeTone; label: string };
const SCHEDULE: Array<{
  time: string;
  meridiem: string;
  name: string;
  service: string;
  status: SchedStatus;
}> = [
  { time: '09:00', meridiem: 'a. m.', name: 'Valentina Ríos', service: 'Limpieza facial', status: { tone: 'positive', label: 'En sala (Checked in)' } },
  { time: '09:45', meridiem: 'a. m.', name: 'Mateo Guzmán', service: 'Consulta general', status: { tone: 'accent', label: 'Confirmado (Confirmed)' } },
  { time: '10:30', meridiem: 'a. m.', name: 'Camila Soto', service: 'Aplicación de toxina', status: { tone: 'attention', label: 'Por confirmar (To confirm)' } },
  { time: '11:15', meridiem: 'a. m.', name: 'Diego Herrera', service: 'Control post-tratamiento', status: { tone: 'neutral', label: 'Agendado (Booked)' } },
  { time: '12:00', meridiem: 'p. m.', name: 'Lucía Fernández', service: 'Peeling químico', status: { tone: 'accent', label: 'Confirmado (Confirmed)' } },
];

const TO_CHARGE = [
  { name: 'Valentina Ríos', detail: 'Limpieza facial · hoy', amount: '$ 850' },
  { name: 'Sofía Navarro', detail: 'Saldo pendiente · 2 días', amount: '$ 1.200' },
  { name: 'Andrés Molina', detail: 'Paquete (3 de 6 sesiones)', amount: 'En paquete' },
];

const TO_CONFIRM = [
  { name: 'Camila Soto', detail: 'Mañana 10:30 a. m.' },
  { name: 'Tomás Vega', detail: 'Jue 14 · 4:00 p. m.' },
];

function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="proto-section-title">
      <h2>{children}</h2>
      {aside}
    </div>
  );
}

function TodaySchedule() {
  return (
    <Card>
      <SectionTitle
        aside={
          <Button variant="ghost" size="sm" leadingIcon={<Plus strokeWidth={2.5} />}>
            Nueva cita
          </Button>
        }
      >
        Agenda de hoy (Today&rsquo;s schedule)
      </SectionTitle>
      <div className="proto-sched">
        {SCHEDULE.map((row) => (
          <div className="proto-sched__row" key={`${row.time}-${row.name}`}>
            <div className="proto-sched__time">
              {row.time}
              <small>{row.meridiem}</small>
            </div>
            <div className="proto-sched__who">
              <strong>{row.name}</strong>
              <span>{row.service}</span>
            </div>
            <div className="proto-sched__meta">
              <Badge tone={row.status.tone} dot>
                {row.status.label}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ToChargePanel() {
  return (
    <Card>
      <SectionTitle aside={<Badge tone="attention">{TO_CHARGE.length}</Badge>}>
        Por cobrar (To charge)
      </SectionTitle>
      <div className="proto-list">
        {TO_CHARGE.map((row) => (
          <div className="proto-list__row" key={row.name}>
            <div className="proto-list__who">
              <strong>{row.name}</strong>
              <span>{row.detail}</span>
            </div>
            <span className="proto-list__amount">{row.amount}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 'var(--space-4)' }}>
        <Button variant="primary" size="sm" fullWidth leadingIcon={<Wallet strokeWidth={2.2} />}>
          Cobrar ahora (Charge)
        </Button>
      </div>
    </Card>
  );
}

function ToConfirmPanel() {
  return (
    <Card>
      <SectionTitle aside={<Badge tone="accent">{TO_CONFIRM.length}</Badge>}>
        Por confirmar (To confirm)
      </SectionTitle>
      <div className="proto-list">
        {TO_CONFIRM.map((row) => (
          <div className="proto-list__row" key={row.name}>
            <div className="proto-list__who">
              <strong>{row.name}</strong>
              <span>{row.detail}</span>
            </div>
            <Button variant="secondary" size="sm" leadingIcon={<Check strokeWidth={2.5} />}>
              Confirmar
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function UpNextPanel() {
  return (
    <Card>
      <SectionTitle
        aside={<Clock strokeWidth={2} width={18} height={18} style={{ color: 'var(--muted)' }} />}
      >
        Próximo (Up next)
      </SectionTitle>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
        <Avatar initials="VR" />
        <div style={{ minWidth: 0 }}>
          <strong style={{ display: 'block', fontSize: '0.94rem', color: 'var(--ink)' }}>
            Valentina Ríos
          </strong>
          <span style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>
            09:00 a. m. · Limpieza facial
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          style={{ marginLeft: 'auto' }}
          leadingIcon={<ChevronRight strokeWidth={2.4} />}
        >
          Abrir
        </Button>
      </div>
    </Card>
  );
}

function PrimitivesGallery() {
  return (
    <Card>
      <SectionTitle
        aside={
          <Badge tone="neutral" dot>
            Sistema (System)
          </Badge>
        }
      >
        Primitivas (Component gallery)
      </SectionTitle>
      <div className="proto-gallery">
        <div className="proto-gallery__group">
          <span className="proto-gallery__label">Botones (Buttons)</span>
          <div className="proto-gallery__row">
            <Button variant="primary" size="md">Primario</Button>
            <Button variant="secondary" size="md">Secundario</Button>
          </div>
          <div className="proto-gallery__row">
            <Button variant="ghost" size="md">Ghost</Button>
            <Button variant="danger" size="md">Eliminar</Button>
          </div>
          <div className="proto-gallery__row">
            <Button variant="primary" size="sm" leadingIcon={<Plus strokeWidth={2.5} />}>
              Pequeño
            </Button>
            <Button variant="secondary" size="sm" disabled>
              Deshabilitado
            </Button>
          </div>
        </div>

        <div className="proto-gallery__group">
          <span className="proto-gallery__label">Estados (Badges)</span>
          <div className="proto-gallery__row">
            <Badge tone="neutral" dot>Agendado</Badge>
            <Badge tone="accent" dot>Confirmado</Badge>
            <Badge tone="positive" dot>En sala</Badge>
          </div>
          <div className="proto-gallery__row">
            <Badge tone="attention" dot>Por confirmar</Badge>
            <Badge tone="danger" dot>Cancelado</Badge>
          </div>
        </div>

        <div className="proto-gallery__group">
          <span className="proto-gallery__label">Tarjeta (Card)</span>
          <Card flat style={{ background: 'var(--surface-sunken)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <span
                style={{
                  display: 'inline-flex',
                  width: 34,
                  height: 34,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--primary-soft)',
                  color: 'var(--primary)',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Stethoscope strokeWidth={2} width={17} height={17} />
              </span>
              <div>
                <strong style={{ fontSize: '0.9rem', color: 'var(--ink)' }}>Superficie elevada</strong>
                <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>Una sola elevación, hairline.</div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Card>
  );
}

function HoyScreen({ onOpenAgenda }: { onOpenAgenda: () => void }) {
  return (
    <>
      <PageHeader
        eyebrow="Jueves, 13 de junio"
        title="Buenos días, Dra. Renata"
        subtitle="Resumen de tu día: citas, cobros y lo que necesita tu atención. (Your day at a glance.)"
        actions={
          <>
            <Button
              variant="secondary"
              size="md"
              leadingIcon={<CalendarDays strokeWidth={2} />}
              onClick={onOpenAgenda}
            >
              Ver agenda
            </Button>
            <Button variant="primary" size="md" leadingIcon={<Plus strokeWidth={2.5} />}>
              Agendar cita (Book)
            </Button>
          </>
        }
      />

      <section className="proto-stats">
        <StatTile
          label="Citas de hoy"
          value="12"
          icon={<CalendarDays strokeWidth={2} />}
          hint="3 ya en sala (checked in)"
          hintTone="positive"
        />
        <StatTile
          label="Ingreso esperado"
          value="$ 18.4k"
          icon={<Wallet strokeWidth={2} />}
          hint={
            <>
              <ArrowUpRight strokeWidth={2.4} /> +8% vs. ayer
            </>
          }
          hintTone="positive"
        />
        <StatTile
          label="Saldos por cobrar"
          value="$ 3.250"
          icon={<CreditCard strokeWidth={2} />}
          hint="5 clientes con saldo"
          hintTone="attention"
        />
        <StatTile
          label="Tareas pendientes"
          value="4"
          icon={<ListChecks strokeWidth={2} />}
          hint="2 vencen hoy"
          hintTone="attention"
        />
      </section>

      <section className="proto-today">
        <TodaySchedule />
        <div className="proto-side">
          <ToChargePanel />
          <ToConfirmPanel />
          <UpNextPanel />
        </div>
      </section>

      <PrimitivesGallery />
    </>
  );
}

// ===========================================================================
//  Screen 2 — Agenda / Calendar (day view, provider columns)
// ===========================================================================
const AGENDA_START_HOUR = 9; // 09:00
const AGENDA_END_HOUR = 18; // 18:00
const AGENDA_SLOT_PX = 64; // pixels per hour

type ApptStatus = 'confirmed' | 'pending' | 'arrived';
const APPT_STATUS: Record<ApptStatus, { tone: BadgeTone; label: string }> = {
  confirmed: { tone: 'accent', label: 'Confirmada' },
  pending: { tone: 'attention', label: 'Por confirmar' },
  arrived: { tone: 'positive', label: 'En sala' },
};

type Appointment = {
  id: string;
  start: number; // decimal hour, e.g. 9.5 = 09:30
  end: number;
  name: string;
  service: string;
  status: ApptStatus;
};

type Provider = { id: string; name: string; role: string; initials: string; appts: Appointment[] };

const AGENDA_PROVIDERS: Provider[] = [
  {
    id: 'renata',
    name: 'Dra. Renata',
    role: 'Dermatología',
    initials: 'RR',
    appts: [
      { id: 'r1', start: 9, end: 9.75, name: 'Valentina Ríos', service: 'Limpieza facial', status: 'arrived' },
      { id: 'r2', start: 10.5, end: 11.25, name: 'Camila Soto', service: 'Aplicación de toxina', status: 'pending' },
      { id: 'r3', start: 12, end: 13, name: 'Lucía Fernández', service: 'Peeling químico', status: 'confirmed' },
      { id: 'r4', start: 16, end: 16.75, name: 'Paula Castro', service: 'Control post-tratamiento', status: 'confirmed' },
    ],
  },
  {
    id: 'martin',
    name: 'Dr. Martín',
    role: 'Medicina general',
    initials: 'MA',
    appts: [
      { id: 'm1', start: 9.75, end: 10.5, name: 'Mateo Guzmán', service: 'Consulta general', status: 'confirmed' },
      { id: 'm2', start: 11.25, end: 12, name: 'Diego Herrera', service: 'Control de rutina', status: 'pending' },
      { id: 'm3', start: 13.5, end: 14.5, name: 'Sofía Navarro', service: 'Valoración inicial', status: 'confirmed' },
    ],
  },
  {
    id: 'lucia',
    name: 'Lic. Lucía',
    role: 'Cosmetología',
    initials: 'LV',
    appts: [
      { id: 'l1', start: 9, end: 10, name: 'Andrés Molina', service: 'Sesión de paquete (3/6)', status: 'arrived' },
      { id: 'l2', start: 10.5, end: 11.5, name: 'Tomás Vega', service: 'Microdermoabrasión', status: 'pending' },
      { id: 'l3', start: 15, end: 16, name: 'Renata Lima', service: 'Hidratación profunda', status: 'confirmed' },
    ],
  },
];

const HOUR_LABELS = Array.from(
  { length: AGENDA_END_HOUR - AGENDA_START_HOUR + 1 },
  (_, i) => AGENDA_START_HOUR + i,
);

function hourLabel(hour: number): string {
  const meridiem = hour < 12 ? 'a. m.' : 'p. m.';
  const h12 = hour > 12 ? hour - 12 : hour;
  return `${String(h12).padStart(2, '0')}:00 ${meridiem}`;
}

function rangeLabel(start: number, end: number): string {
  const fmt = (value: number) => {
    const h = Math.floor(value);
    const m = Math.round((value - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  return `${fmt(start)}–${fmt(end)}`;
}

function AppointmentCard({ appt }: { appt: Appointment }) {
  const status = APPT_STATUS[appt.status];
  const top = (appt.start - AGENDA_START_HOUR) * AGENDA_SLOT_PX;
  const height = (appt.end - appt.start) * AGENDA_SLOT_PX;
  return (
    <article
      className={`proto-appt proto-appt--${appt.status}`}
      style={{ top: `${top}px`, height: `${height - 6}px` }}
    >
      <div className="proto-appt__top">
        <span className="proto-appt__time">{rangeLabel(appt.start, appt.end)}</span>
        <Badge tone={status.tone} dot>
          {status.label}
        </Badge>
      </div>
      <strong className="proto-appt__name">{appt.name}</strong>
      <span className="proto-appt__service">{appt.service}</span>
    </article>
  );
}

function ProviderColumn({ provider }: { provider: Provider }) {
  return (
    <div className="proto-agenda__col">
      <div className="proto-agenda__colhead">
        <Avatar initials={provider.initials} size="sm" />
        <div>
          <strong>{provider.name}</strong>
          <small>{provider.role}</small>
        </div>
      </div>
      <div
        className="proto-agenda__track"
        style={{ height: `${(AGENDA_END_HOUR - AGENDA_START_HOUR) * AGENDA_SLOT_PX}px` }}
      >
        {HOUR_LABELS.slice(0, -1).map((hour) => (
          <div
            key={hour}
            className="proto-agenda__gridline"
            style={{ top: `${(hour - AGENDA_START_HOUR) * AGENDA_SLOT_PX}px` }}
          />
        ))}
        {provider.appts.map((appt) => (
          <AppointmentCard key={appt.id} appt={appt} />
        ))}
      </div>
    </div>
  );
}

function AgendaScreen() {
  return (
    <>
      <PageHeader
        eyebrow="Agenda (Calendar) · Vista de día"
        title="Jueves, 13 de junio"
        subtitle="Tres consultorios, un solo día. Cada columna es un profesional. (Day view by provider.)"
        actions={
          <>
            <div className="proto-daynav">
              <button type="button" className="proto-iconbtn" aria-label="Día anterior (Previous day)">
                <ChevronLeft strokeWidth={2.2} />
              </button>
              <span className="proto-daynav__today">Hoy</span>
              <button type="button" className="proto-iconbtn" aria-label="Día siguiente (Next day)">
                <ChevronRight strokeWidth={2.2} />
              </button>
            </div>
            <Button variant="primary" size="md" leadingIcon={<Plus strokeWidth={2.5} />}>
              Nueva cita (New appointment)
            </Button>
          </>
        }
      />

      <Card padded={false} className="proto-agenda-card">
        <div className="proto-agenda">
          {/* time axis */}
          <div className="proto-agenda__axis">
            <div className="proto-agenda__axishead" />
            <div
              className="proto-agenda__axistrack"
              style={{ height: `${(AGENDA_END_HOUR - AGENDA_START_HOUR) * AGENDA_SLOT_PX}px` }}
            >
              {HOUR_LABELS.map((hour) => (
                <span
                  key={hour}
                  className="proto-agenda__axislabel"
                  style={{ top: `${(hour - AGENDA_START_HOUR) * AGENDA_SLOT_PX}px` }}
                >
                  {hourLabel(hour)}
                </span>
              ))}
            </div>
          </div>
          {/* provider columns */}
          <div className="proto-agenda__cols">
            {AGENDA_PROVIDERS.map((provider) => (
              <ProviderColumn key={provider.id} provider={provider} />
            ))}
          </div>
        </div>
      </Card>

      <div className="proto-legend">
        <span className="proto-legend__label">Estados (Status):</span>
        <Badge tone="accent" dot>Confirmada (Confirmed)</Badge>
        <Badge tone="attention" dot>Por confirmar (Pending)</Badge>
        <Badge tone="positive" dot>En sala (Arrived)</Badge>
      </div>
    </>
  );
}

// ===========================================================================
//  Screen 3 — Clientes / Patients (list + table)
// ===========================================================================
type BalanceTone = 'positive' | 'attention' | 'muted';
type Patient = {
  id: string;
  name: string;
  phone: string;
  lastVisit: string;
  balance: string;
  balanceTone: BalanceTone;
  tags: Array<{ label: string; tone: BadgeTone }>;
};

const PATIENTS: Patient[] = [
  { id: 'p1', name: 'Valentina Ríos', phone: '+52 55 1234 5678', lastVisit: '13 jun 2026', balance: '$ 850', balanceTone: 'attention', tags: [{ label: 'Frecuente', tone: 'accent' }, { label: 'Facial', tone: 'neutral' }] },
  { id: 'p2', name: 'Mateo Guzmán', phone: '+52 55 2345 6789', lastVisit: '11 jun 2026', balance: 'Al día', balanceTone: 'positive', tags: [{ label: 'Nuevo', tone: 'positive' }] },
  { id: 'p3', name: 'Camila Soto', phone: '+52 81 3456 7890', lastVisit: '06 jun 2026', balance: '$ 1.200', balanceTone: 'attention', tags: [{ label: 'Toxina', tone: 'neutral' }] },
  { id: 'p4', name: 'Diego Herrera', phone: '+52 33 4567 8901', lastVisit: '29 may 2026', balance: 'Al día', balanceTone: 'positive', tags: [{ label: 'Control', tone: 'neutral' }] },
  { id: 'p5', name: 'Lucía Fernández', phone: '+52 55 5678 9012', lastVisit: '22 may 2026', balance: 'Al día', balanceTone: 'positive', tags: [{ label: 'VIP', tone: 'accent' }, { label: 'Peeling', tone: 'neutral' }] },
  { id: 'p6', name: 'Andrés Molina', phone: '+52 55 6789 0123', lastVisit: '18 may 2026', balance: 'En paquete', balanceTone: 'muted', tags: [{ label: 'Paquete 3/6', tone: 'accent' }] },
  { id: 'p7', name: 'Sofía Navarro', phone: '+52 222 789 0123', lastVisit: '02 may 2026', balance: '$ 1.200', balanceTone: 'attention', tags: [{ label: 'Saldo', tone: 'attention' }] },
  { id: 'p8', name: 'Tomás Vega', phone: '+52 55 8901 2345', lastVisit: '14 abr 2026', balance: 'Al día', balanceTone: 'positive', tags: [{ label: 'Inactivo', tone: 'neutral' }] },
];

const PATIENT_FILTERS = [
  { key: 'all', label: 'Todos (All)', count: 248 },
  { key: 'balance', label: 'Con saldo (Balance)', count: 12 },
  { key: 'today', label: 'Citados hoy (Today)', count: 8 },
  { key: 'new', label: 'Nuevos (New)', count: 5 },
];

const PATIENT_COLUMNS: TableColumn[] = [
  { key: 'name', header: 'Cliente (Name)' },
  { key: 'phone', header: 'Teléfono (Phone)' },
  { key: 'lastVisit', header: 'Última visita (Last visit)' },
  { key: 'balance', header: 'Saldo (Balance)', align: 'right' },
  { key: 'tags', header: 'Etiquetas (Tags)' },
];

function balanceClass(tone: BalanceTone): string {
  if (tone === 'attention') return 'proto-balance proto-balance--attention';
  if (tone === 'positive') return 'proto-balance proto-balance--positive';
  return 'proto-balance proto-balance--muted';
}

function PatientCell({ patient, column }: { patient: Patient; column: TableColumn }) {
  switch (column.key) {
    case 'name':
      return (
        <div className="proto-cell-name">
          <Avatar initials={initialsOf(patient.name)} size="sm" />
          <strong>{patient.name}</strong>
        </div>
      );
    case 'phone':
      return (
        <span className="proto-cell-phone">
          <Phone strokeWidth={2} width={14} height={14} />
          {patient.phone}
        </span>
      );
    case 'lastVisit':
      return <span className="proto-cell-muted">{patient.lastVisit}</span>;
    case 'balance':
      return <span className={balanceClass(patient.balanceTone)}>{patient.balance}</span>;
    case 'tags':
      return (
        <div className="proto-cell-tags">
          {patient.tags.map((tag) => (
            <Badge key={tag.label} tone={tag.tone}>
              {tag.label}
            </Badge>
          ))}
        </div>
      );
    default:
      return null;
  }
}

function ClientesScreen() {
  const [activeFilter, setActiveFilter] = useState('all');

  return (
    <>
      <PageHeader
        eyebrow="Clientes (Clients) · Directorio"
        title="Directorio de clientes"
        subtitle="248 personas en tu clínica. Busca, filtra y abre una ficha en un toque. (Search, filter, open a record.)"
        actions={
          <Button variant="primary" size="md" leadingIcon={<Plus strokeWidth={2.5} />}>
            Nuevo cliente (New client)
          </Button>
        }
      />

      <Card>
        <div className="proto-clients-toolbar">
          <label className="proto-search proto-search--inline">
            <Search strokeWidth={2} />
            <input placeholder="Buscar por nombre o teléfono… (Search)" aria-label="Buscar cliente" />
          </label>
          <div className="proto-chips">
            {PATIENT_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={
                  activeFilter === filter.key ? 'proto-chip proto-chip--active' : 'proto-chip'
                }
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
                <span className="proto-chip__count">{filter.count}</span>
              </button>
            ))}
            <button type="button" className="proto-chip proto-chip--icon" aria-label="Más filtros (More filters)">
              <SlidersHorizontal strokeWidth={2} width={15} height={15} />
            </button>
          </div>
        </div>

        <Table<Patient>
          columns={PATIENT_COLUMNS}
          rows={PATIENTS}
          rowKey={(patient) => patient.id}
          renderCell={(patient, column) => <PatientCell patient={patient} column={column} />}
          onRowClick={() => {
            /* prototype: would open the patient record */
          }}
        />

        <div className="proto-clients-foot">
          <span>Mostrando 8 de 248 clientes</span>
          <div className="proto-pager">
            <button type="button" className="proto-iconbtn" aria-label="Página anterior">
              <ChevronLeft strokeWidth={2.2} />
            </button>
            <button type="button" className="proto-iconbtn" aria-label="Página siguiente">
              <ChevronRight strokeWidth={2.2} />
            </button>
          </div>
        </div>
      </Card>
    </>
  );
}

// ===========================================================================
//  Placeholder screen (Cobros, Mensajes, Ajustes, Más)
// ===========================================================================
const COMING_SOON_COPY: Record<string, { title: string; sub: string; icon: typeof Home }> = {
  cobros: { title: 'Cobros (Billing)', sub: 'Facturación, pagos y paquetes vivirán aquí.', icon: CreditCard },
  mensajes: { title: 'Mensajes (Messages)', sub: 'Recordatorios y conversaciones con clientes.', icon: MessageSquare },
  ajustes: { title: 'Ajustes (Settings)', sub: 'Equipo, servicios, horarios y preferencias.', icon: Settings },
  reportes: { title: 'Reportes (Reports)', sub: 'Ingresos, ocupación y retención de clientes.', icon: ArrowUpRight },
  inventario: { title: 'Inventario (Inventory)', sub: 'Insumos, lotes y alertas de stock.', icon: ListChecks },
};

function ComingSoonScreen({ navKey }: { navKey: NavKey }) {
  const copy = COMING_SOON_COPY[navKey] ?? {
    title: 'Próximamente',
    sub: 'Esta sección llegará pronto.',
    icon: Sparkles,
  };
  const Icon = copy.icon;
  return (
    <>
      <PageHeader eyebrow="Prototipo (Prototype)" title={copy.title} subtitle={copy.sub} />
      <Card className="proto-soon">
        <span className="proto-soon__icon">
          <Icon strokeWidth={1.8} />
        </span>
        <h3 className="proto-soon__title">En el prototipo: próximamente</h3>
        <p className="proto-soon__sub">
          Esta pantalla aún no forma parte de la demostración. Las pantallas{' '}
          <strong>Hoy</strong>, <strong>Agenda</strong> y <strong>Clientes</strong> muestran la
          dirección del nuevo sistema. (In the prototype: coming soon.)
        </p>
        <Badge tone="neutral" dot>
          Próximamente (Coming soon)
        </Badge>
      </Card>
    </>
  );
}

// ===========================================================================
//  Screen router
// ===========================================================================
function ScreenSwitch({ active, onNavigate }: { active: NavKey; onNavigate: (key: NavKey) => void }) {
  switch (active) {
    case 'hoy':
      return <HoyScreen onOpenAgenda={() => onNavigate('agenda')} />;
    case 'agenda':
      return <AgendaScreen />;
    case 'clientes':
      return <ClientesScreen />;
    default:
      return <ComingSoonScreen navKey={active} />;
  }
}

// ===========================================================================
//  Page
// ===========================================================================
export default function DesignPrototypePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const [activeNav, setActiveNav] = useState<NavKey>('hoy');
  const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  return (
    <div className="calm proto" data-theme={theme}>
      <div className="proto-shell">
        <NavRail active={activeNav} onSelect={setActiveNav} />
        <main className="proto-main">
          <TopBar theme={theme} onToggleTheme={toggleTheme} />
          <div className="proto-content">
            <ScreenSwitch active={activeNav} onNavigate={setActiveNav} />

            <p className="proto-foot-note">
              Prototipo de referencia · Sistema clínico · claro y profesional. No conectado a datos
              reales. (Reference prototype — not wired to real data.)
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
